import React from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { AnalysisReading, BudgetReading, BudgetVerdict, SectionHead } from '@/components/dashboard';
import { FilterBar } from '@/components/filters';
import { Button } from '@/components/ui/button';
import { useBudgetSummary } from '@/hooks/useBudgets';
import { useCategories } from '@/hooks/useCategories';
import { useFilters } from '@/hooks/useFilters';
import { useReport } from '@/hooks/useReports';
import { focusSection, useScrollToHash } from '@/hooks/useScrollToHash';
import { DASHBOARD_SECTIONS } from '@/lib/constants';
import { formatDateRange, formatPeriodLabel, monthBounds, toDateString, toPeriodString } from '@/lib/format';
import {
  DASHBOARD_BUDGET_HEADING,
  DASHBOARD_BUDGET_MONTH_ONLY,
  DASHBOARD_BACK_TO_MONTH,
  DASHBOARD_CASH_FLOW_HEADING,
  DASHBOARD_SKIP_TO_BUDGET,
  budgetSectionAside,
} from '@/lib/translations';

// Painel reads one period two ways, stacked under one period bar: the cash flow — what came in and went out, any
// range — and beneath it the budget — am I inside my limits, one month. The cash flow leads because it needs
// nothing from the user, while the budget needs their categories classified. This page owns the period and the
// three queries; each section only renders what it is handed and has its own loading, empty and error states,
// so one failing never blanks the other.
const Dashboard: React.FC = () => {
  const filters = useFilters();
  const { applied } = filters;

  // The budget is a share of one month's income, so a custom range has none: its section says so instead of
  // asking /budgets about it. (In range mode `applied.period` is only the month the range starts in.)
  const inRange = applied.mode === 'range';

  const categoriesQuery = useCategories();
  const budgetMonth = monthBounds(applied.period);
  const summaryQuery = useBudgetSummary({
    from: toDateString(budgetMonth.from),
    to: toDateString(budgetMonth.to),
    enabled: !inRange,
  });
  const reportQuery = useReport({ from: toDateString(applied.from), to: toDateString(applied.to) });

  // A link can name a section (`/dashboard#budget`). The cash flow above it decides where that section ends up,
  // so the scroll waits for it.
  useScrollToHash(!reportQuery.isPending);

  return (
    <Layout>
      <div className="space-y-8">
        {/* For a keyboard: past the period bar and both charts straight to the budget. Invisible until focused. */}
        <a
          href={`#${DASHBOARD_SECTIONS.budget}`}
          onClick={(event) => {
            event.preventDefault();
            focusSection(DASHBOARD_SECTIONS.budget);
          }}
          className="sr-only rounded-md bg-background px-3 py-2 text-sm font-semibold text-foreground ring-2 ring-ring focus:not-sr-only focus:absolute focus:z-50"
        >
          {DASHBOARD_SKIP_TO_BUDGET}
        </a>
        <PageHeader
          title="Painel"
          eyebrow="Onde você está agora"
          action={
            <Link
              to="/transactions/add"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Plus className="h-3.5 w-3.5" />
              Nova Transação
            </Link>
          }
        />

        {/* The verdict belongs to the period bar: it reads the month the bar has picked. */}
        <div className="space-y-4">
          <FilterBar filters={filters} />
          {!inRange && (
            <BudgetVerdict period={applied.period} summaryQuery={summaryQuery} categoriesQuery={categoriesQuery} />
          )}
        </div>

        <section aria-labelledby={DASHBOARD_SECTIONS.cashFlow} className="space-y-6">
          <SectionHead
            id={DASHBOARD_SECTIONS.cashFlow}
            title={DASHBOARD_CASH_FLOW_HEADING}
            aside={inRange ? formatDateRange(applied.from, applied.to) : formatPeriodLabel(applied.period)}
          />
          <AnalysisReading reportQuery={reportQuery} />
        </section>

        {/* The extra top padding is the divider's air: more space above a head than below it. */}
        <section aria-labelledby={DASHBOARD_SECTIONS.budget} className="space-y-6 pt-6">
          <SectionHead
            id={DASHBOARD_SECTIONS.budget}
            title={DASHBOARD_BUDGET_HEADING}
            aside={budgetSectionAside(inRange ? undefined : formatPeriodLabel(applied.period))}
          />
          {inRange ? (
            <div className="flex flex-col items-start gap-3">
              <p className="max-w-xl text-sm text-muted-foreground">{DASHBOARD_BUDGET_MONTH_ONLY}</p>
              {/* The month the range starts in — the same return the period bar's "Voltar para o mês" makes. */}
              <Button variant="secondary" size="sm" onClick={() => filters.applyMonth(applied.period)}>
                {DASHBOARD_BACK_TO_MONTH}
              </Button>
            </div>
          ) : (
            <BudgetReading
              summaryQuery={summaryQuery}
              categoriesQuery={categoriesQuery}
              period={toPeriodString(applied.period)}
            />
          )}
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
